/**
 * premiumLocks.js — Premium Collection Types gating helpers.
 *
 * Reads the `plan` flag on the shared Collection Type Registry
 * (`lib/collectionTypes.js`) — no new schema is introduced. When a user
 * "upgrades" through the placeholder Premium Upsell modal, we record
 * the collection id in `localStorage.cdi:premium_locked_collections`
 * so the lock badge stays visible on the card (spec: the collection is
 * "marked isPremiumLocked" even after creation).
 *
 * Purely visual + UX gating — real payments + unlock arrive in Phase 9.
 */
import { getCollectionType } from "@/lib/collectionTypes";

export const PREMIUM_LOCKED_KEY = "cdi:premium_locked_collections";

/** True when the given type id is flagged `plan === "premium"`. */
export function isPremiumType(typeId) {
  const entry = getCollectionType(typeId);
  return !!entry && entry.plan === "premium";
}

/**
 * Read the client-side set of collection ids the user chose to
 * "upgrade" through the placeholder flow. Guards against corrupt or
 * private-mode reads by returning an empty Set.
 */
export function readPremiumLockedIds() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(PREMIUM_LOCKED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : []);
  } catch { return new Set(); }
}

/** Persist a newly-created "upgraded" collection id. Idempotent. */
export function markCollectionPremiumLocked(id) {
  if (!id || typeof window === "undefined") return;
  try {
    const cur = readPremiumLockedIds();
    if (cur.has(id)) return;
    cur.add(id);
    window.localStorage.setItem(PREMIUM_LOCKED_KEY, JSON.stringify([...cur]));
  } catch { /* quota / private mode — best effort */ }
}

/**
 * Whether a collection should render the gold lock badge. True when
 * EITHER the type is `premium` in the registry OR the id was explicitly
 * flagged during the placeholder upgrade flow. This is a purely visual
 * predicate — it does not gate reads or navigation.
 */
export function isCollectionPremiumLocked(collection, lockedIds) {
  if (!collection) return false;
  if (isPremiumType(collection.type_id)) return true;
  const set = lockedIds || readPremiumLockedIds();
  return set.has(collection.id);
}
